import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CreditCard, Receipt } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useAuthStore } from "../state/authStore";
import { postJSON } from "../lib/api";

const MOCK_INVOICES = [
    { id: "inv_001", date: "2026-04-17", amount: "$49.00", status: "paid" },
    { id: "inv_002", date: "2026-03-17", amount: "$49.00", status: "paid" },
];

export const MockPortal = () => {
    const navigate = useNavigate();
    const { user, refresh } = useAuthStore();
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const cancel = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await postJSON("/billing/mock/cancel", {});
            await refresh();
            setMsg("Subscription canceled.");
        } catch (e) {
            setMsg(e.message || "Could not cancel.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-12 mx-auto max-w-2xl px-5" data-testid="mock-portal-page">
            <div
                className="mb-6 rounded-md border border-accent-amber/40 bg-accent-amber/[0.06] px-3 py-2.5 text-[12px] font-mono uppercase tracking-[0.22em] text-accent-amber flex items-center gap-2"
                data-testid="mock-portal-banner"
            >
                <AlertTriangle className="w-3.5 h-3.5" />
                MOCK BILLING PORTAL — REPLACE WITH STRIPE PORTAL FOR PRODUCTION
            </div>

            <Reveal>
                <div className="rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-7">
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        Billing
                    </p>
                    <h1 className="mt-3 font-display font-semibold text-3xl tracking-tight" data-testid="mock-portal-title">
                        Manage your subscription
                    </h1>
                    <p className="mt-2 text-text-muted text-[13.5px]">
                        Plan: <span className="text-text-primary font-medium">{user?.subscription_plan?.toUpperCase()}</span>{" "}
                        · Status:{" "}
                        <span className="text-text-primary font-medium" data-testid="mock-portal-status">
                            {user?.subscription_status}
                        </span>
                    </p>

                    {msg && (
                        <div
                            className="mt-4 text-[12.5px] text-accent-mint bg-accent-mint/5 border border-accent-mint/20 rounded-md px-3 py-2"
                            data-testid="mock-portal-message"
                        >
                            {msg}
                        </div>
                    )}

                    <div className="mt-6">
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-3">
                            Recent invoices
                        </p>
                        <ul className="divide-y divide-white/[0.05] border border-white/[0.06] rounded-lg overflow-hidden" data-testid="mock-portal-invoices">
                            {MOCK_INVOICES.map((inv) => (
                                <li key={inv.id} className="flex items-center justify-between px-4 py-3 bg-canvas/40">
                                    <div className="flex items-center gap-2.5">
                                        <Receipt className="w-3.5 h-3.5 text-text-dim" />
                                        <span className="text-[13px] text-text-primary font-mono">{inv.id}</span>
                                        <span className="text-[12px] text-text-muted">· {inv.date}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-[13px] text-text-primary font-semibold">{inv.amount}</span>
                                        <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-accent-mint">
                                            {inv.status}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link
                            to="/dashboard"
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-white/[0.08] text-[13px] text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors duration-200"
                            data-testid="mock-portal-back"
                        >
                            ← Back to dashboard
                        </Link>
                        <button
                            type="button"
                            onClick={cancel}
                            disabled={busy || user?.subscription_status !== "active"}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-accent-red/40 text-[13px] text-accent-red hover:bg-accent-red/[0.05] disabled:opacity-40 transition-colors duration-200"
                            data-testid="mock-portal-cancel"
                        >
                            <CreditCard className="w-3.5 h-3.5" />
                            Cancel subscription
                        </button>
                    </div>
                </div>
            </Reveal>
        </div>
    );
};

export default MockPortal;
